import { html } from "@lit-labs/ssr";
import { nothing } from "lit";

import { unsafeHTML } from "lit/directives/unsafe-html.js";

import { stylesForComponents } from "../outer-layout/utils.js";

import { asyncLocalStorage } from "./async-local-storage.js";

export class ServerComponent {
  static stylesInHead = true;
  static legacy = false;
  /** @type {string | undefined} */
  static inlineScript;

  /** `true` if we're rendering in a renderSimplified context */
  simplifiedMode = false;

  /**
   * @template {typeof ServerComponent} T
   * @this {T}
   * @param {Parameters<InstanceType<T>["render"]>} args
   * @returns {ReturnType<InstanceType<T>["render"]> | import("@lit").TemplateResult | import("@lit").nothing }
   */
  static render(...args) {
    const asyncStore = asyncLocalStorage.getStore();
    if (!asyncStore) {
      throw new Error("asyncLocalStorage missing");
    }

    const component = new this();

    if ("renderSimplified" in asyncStore) {
      component.simplifiedMode = true;
      return component.renderSimplified(...args);
    }

    const { componentsUsed, componentsWithStylesInHead, compilationStats } =
      asyncStore;
    const componentUsedBefore = componentsUsed.has(this.name);
    componentsUsed.add(this.name);
    if (this.stylesInHead) {
      componentsWithStylesInHead.add(this.name);
    }
    if (this.legacy) {
      componentsUsed.add("legacy");
    }

    let res = component.render(...args);

    if (!res || res === nothing) {
      if (!componentUsedBefore) {
        componentsUsed.delete(this.name);
        componentsWithStylesInHead.delete(this.name);
      }
      return nothing;
    }

    const { inlineScript } = this;
    if (inlineScript) {
      res = html`${res}${unsafeHTML(`<script>${inlineScript}</script>`)}`;
    }

    if (!this.stylesInHead) {
      const styles = stylesForComponents([this.name], compilationStats.client);
      if (styles.length > 0) {
        // render block in Gecko and WebKit with empty script tag following link
        // https://github.com/chrishtr/rendering/blob/master/stylesheet-loading-behavior.md
        return html`${styles.map(
          (path) =>
            html`<link rel="stylesheet" href=${path} fetchpriority="high" />
              <script></script>`,
        )}${res}`;
      }
    }
    return res;
  }

  /* eslint-disable jsdoc/reject-any-type -- abstract render is overridden by subclasses (needs a supertype) yet consumed via generic `ReturnType<...>` (needs `any`); neither `unknown` nor `never` satisfies both */
  /**
   * @abstract
   * @param {...any} _args
   * @returns {any}
   */
  /* eslint-enable jsdoc/reject-any-type */
  render(..._args) {
    throw new Error("Must be implemented by subclass");
  }

  /* eslint-disable jsdoc/reject-any-type -- abstract render is overridden by subclasses (needs a supertype) yet consumed via generic `ReturnType<...>` (needs `any`); neither `unknown` nor `never` satisfies both */
  /**
   * @param {...any} args
   * @returns {any}
   */
  /* eslint-enable jsdoc/reject-any-type */
  renderSimplified(...args) {
    return this.render(...args);
  }
}
